import {
  CoreApi,
  Dimension,
  Dimensions,
  OuterMeasurements,
  Product,
  ProductEntity
} from '@inter-ikea-storageone/range';
import { HierarchyUtils, ComponentIds, Entity, ProductPartComponent } from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { ProductIdentifiers, ProductType } from '@inter-ikea-storageone/range';
import { getCornerSolutionPlacement, getMeasurementsForWall, RoomCorners } from './utils';

type PartSizes = {
  [dim: string]: string[];
};

type AllowedSizes = {
  [partName: string]: PartSizes;
};

export const getFramePart = (partKey: string) => (entity: Entity) =>
  entity.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID) &&
  entity.getComponent<ProductPartComponent>(ComponentIds.PRODUCT_PART_COMPONENT_ID)?.partKey ===
    partKey;

export const getNormalFrameMeasurements = (entity: Entity) => {
  const normalFrame = HierarchyUtils.children(entity).find((child: Entity) =>
    getFramePart('normal-frame')(child)
  );
  return (
    normalFrame?.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size || { width: 0, height: 0, depth: 0 }
  );
};

export const getAddOnMeasurements = (entity: Entity) => {
  const hierarchy = HierarchyUtils.hierarchy(entity);
  const getAddOnPart = getFramePart('corner-frame');
  const addOnFrame = hierarchy.find((entity: Entity) => getAddOnPart(entity));
  return (
    addOnFrame?.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size || { width: 0, height: 0, depth: 0 }
  );
};

export const matchDimensions = (dimA: Dimensions, dimB: Dimensions, excludeDim?: Dimension) => {
  const dimensions: (keyof Dimensions)[] = ['width', 'depth', 'height'];
  return dimensions.filter(f => f !== excludeDim).every(dim => dimA[dim] === dimB[dim]);
};

export const getDisabledSizesDefault = (): AllowedSizes => ({
  addOnFrame: { depth: [] },
  frame: { depth: [], width: [] }
});

export const isSameOrientation = (productA: Product, productB: Product) =>
  (productA.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT) &&
    productB.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT)) ||
  (!productA.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT) &&
    !productB.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT));

export type WallMeasurements = {
  front: OuterMeasurements | null;
  left: OuterMeasurements | null;
  right: OuterMeasurements | null;
  back: OuterMeasurements | null;
};

export const validateCornerSolutions = (
  cornerSolutions: Product[],
  products: Product[],
  selectedEntityMeasurements: OuterMeasurements,
  selectedProductMeasurements: {
    selectedAddOnFrameSize: Dimensions;
    selectedNormalFrameSize: Dimensions;
  },
  wallMeasuremens: WallMeasurements,
  cornerSolutionPlacement: RoomCorners,
  isLeftOrientation: boolean
) => {
  const result = getDisabledSizesDefault();
  const { front, left, right, back } = wallMeasuremens;
  const { selectedAddOnFrameSize, selectedNormalFrameSize } = selectedProductMeasurements;
  cornerSolutions.forEach(cornerSolution => {
    const [normalFrameId, addOnFrameId] = cornerSolution.coreProductId.split('-');
    const size = cornerSolution.communicatedDimensions;
    const normalFramePart = cornerSolution.parts.find(part => part.id === normalFrameId);
    const addOnPart = cornerSolution.parts.find(part => part.id === addOnFrameId);
    const normalFrameSize = products.find(p => p.coreProductId === normalFramePart?.id)
      ?.communicatedDimensions;
    const addOnSize = products.find(p => p.coreProductId === addOnPart?.id)?.communicatedDimensions;

    if (!addOnSize || !normalFrameSize) return result;

    const isSameAddOn = matchDimensions(selectedAddOnFrameSize, addOnSize);
    const isSameNormalFrame = matchDimensions(selectedNormalFrameSize, normalFrameSize);

    // Measurements of frames on the wall (front or back) the corner solution is placed on
    const wallMeasurementsX =
      cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'front-right'
        ? front
        : back;

    if (wallMeasurementsX) {
      // Figure out dimension of the corner solution along front/back wall
      const dimensionX =
        (isLeftOrientation &&
          (cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'back-right')) ||
        (!isLeftOrientation &&
          (cornerSolutionPlacement === 'front-right' || cornerSolutionPlacement === 'back-left'))
          ? size.width
          : size.depth;

      // Is the corner frame placed on any of the corners of the left wall
      const isOnLeftWall =
        cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'back-left';

      // Calculate the x value on where the corner solution will stretch to on front/back wall
      const intersectionPoint = isOnLeftWall
        ? dimensionX + selectedEntityMeasurements.min.x
        : selectedEntityMeasurements.max.x - dimensionX;

      // Calculate if the corner solution will intersect with other frames on the front/back wall
      const willIntersect = isOnLeftWall
        ? intersectionPoint > wallMeasurementsX.min.x
        : wallMeasurementsX.max.x > intersectionPoint;

      if (willIntersect) {
        if (isSameAddOn && matchDimensions(selectedNormalFrameSize, normalFrameSize, 'width')) {
          result.frame.width.push(normalFrameSize.width.toString());
        }
        if (isSameAddOn && matchDimensions(selectedNormalFrameSize, normalFrameSize, 'depth')) {
          result.frame.depth.push(normalFrameSize.depth.toString());
        }
        if (isSameNormalFrame && matchDimensions(selectedAddOnFrameSize, addOnSize, 'depth')) {
          result.addOnFrame.depth.push(addOnSize.depth.toString());
        }
      }
    }

    // Measurements of frames on the wall (left or right) the corner solution is placed on
    const wallMeasurementsZ =
      cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'back-left'
        ? left
        : right;

    if (wallMeasurementsZ) {
      // Figure out dimension of the corner solution along left/right wall
      const dimensionZ =
        (isLeftOrientation &&
          (cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'back-right')) ||
        (!isLeftOrientation &&
          (cornerSolutionPlacement === 'front-right' || cornerSolutionPlacement === 'back-left'))
          ? size.depth
          : size.width;

      // Is the corner frame placed on any of the corners of the front wall
      const isOnFrontWall =
        cornerSolutionPlacement === 'front-left' || cornerSolutionPlacement === 'front-right';

      // Calculate the z value on where the corner solution will stretch to on left/right wall
      const intersectionPoint = isOnFrontWall
        ? dimensionZ
        : selectedEntityMeasurements.max.z - dimensionZ;

      // Calculate if the corner solution will intersect with other frames on the left/right wall
      const willIntersect = isOnFrontWall
        ? intersectionPoint > wallMeasurementsZ.min.z
        : wallMeasurementsZ.max.z > intersectionPoint;

      if (willIntersect) {
        if (isSameAddOn && matchDimensions(selectedNormalFrameSize, normalFrameSize, 'width')) {
          result.frame.width.push(normalFrameSize.width.toString());
        }
        if (isSameAddOn && matchDimensions(selectedNormalFrameSize, normalFrameSize, 'depth')) {
          result.frame.depth.push(normalFrameSize.depth.toString());
        }
        if (isSameNormalFrame && matchDimensions(selectedAddOnFrameSize, addOnSize, 'depth')) {
          result.addOnFrame.depth.push(addOnSize.depth.toString());
        }
      }
    }
  });
  return result;
};

export const findInvalidReplacements = (
  coreApi: CoreApi,
  selectedProductEntity: ProductEntity,
  products: Product[],
  orientations: { left: string; right: string }
) => {
  const selectedEntity = coreApi.entities.find(selectedProductEntity.entityId);
  const selectedProduct = products.find(p => p.coreProductId === selectedProductEntity.productId);

  if (!selectedEntity || !selectedProduct) return getDisabledSizesDefault();

  const isLeftOrientation = orientations.left === selectedProduct.coreProductId;

  const cornerSolutions = products.filter(
    p =>
      p.type === ProductType.PAX_CORNER_SOLUTION_ASM &&
      p.communicatedDimensions.height === selectedProduct.communicatedDimensions.height &&
      isSameOrientation(selectedProduct, p)
  );
  const cornerSolutionPlacement = getCornerSolutionPlacement(coreApi, selectedEntity, orientations);

  const getMeasurements = getMeasurementsForWall(coreApi, selectedEntity);
  const wallMeasurements = {
    front: getMeasurements('front'),
    left: getMeasurements('left'),
    right: getMeasurements('right'),
    back: getMeasurements('back')
  };

  const selectedEntityMeasurements = coreApi.entities.getMeasurements([selectedEntity]);

  if (!selectedEntityMeasurements) return getDisabledSizesDefault();

  const selectedProductMeasurements = {
    selectedAddOnFrameSize: getAddOnMeasurements(selectedEntity),
    selectedNormalFrameSize: getNormalFrameMeasurements(selectedEntity)
  };

  const result = validateCornerSolutions(
    cornerSolutions,
    products,
    selectedEntityMeasurements,
    selectedProductMeasurements,
    wallMeasurements,
    cornerSolutionPlacement,
    isLeftOrientation
  );
  return result;
};
